// Which infrastructure GENERATION the portal talks to. The supplier portal is the one consumer that can
// point at EITHER stack — the deviation that lets the rebuilt .NET stack run in PARALLEL with the legacy one:
//
//   • OLD  — the legacy Lambda API backed by MySQL + Mongo (https://dev-api.repair.exchange). This is what
//            the deployed https://dev-portal.repair.exchange/ uses; it is left entirely untouched.
//   • NEW  — the rebuilt .NET BFF backed by Postgres (http://localhost:8081 in the local co-existence stack).
//            The Mongo→Postgres replacement lives ONLY here, so the new stack is the only way to exercise it.
//
// `old`/`new` are the first-class semantic names; `legacy`/`local` are retained as back-compat aliases
// (existing .env files, the Docker build arg, and config.test.ts use them).
export const API_TARGETS = {
    old: 'https://dev-api.repair.exchange', // OLD infra: Lambda + MySQL + Mongo
    legacy: 'https://dev-api.repair.exchange', // alias of `old`
    new: 'http://localhost:8081', // NEW infra: .NET BFF + Postgres
    local: 'http://localhost:8081', // alias of `new`
} as const;

export type ApiTarget = keyof typeof API_TARGETS;

// Default to the OLD infra: the rebuilt stack is opt-in (it is not the production backend), and the deployed
// portal bakes an explicit VITE_PORTAL_API_HOST at build time, so this default only affects bare `npm run dev`.
const DEFAULT_TARGET: ApiTarget = 'old';

export const resolvePortalApiHost = (): string => {
    const explicitHost = import.meta.env.VITE_PORTAL_API_HOST?.trim();
    if (explicitHost) return explicitHost;

    const target = import.meta.env.VITE_API_TARGET?.trim();
    if (target && target in API_TARGETS) return API_TARGETS[target as ApiTarget];

    return API_TARGETS[DEFAULT_TARGET];
};

export const PORTAL_API_HOST: string = resolvePortalApiHost();

export const ROUTES = {
    LOGIN: '/login',
    REFRESH: '/refresh-token',
    RESET_PASSWORD: '/forgot-password',

    JOBS_LIST: '/rodx/jobs',
    JOB: (id: string) => `/rodx/job/${id}`,
    JOB_CREATE: '/rodx/job',
    JOB_UPDATE: (id: string) => `/rodx/job/${id}`,
    JOB_DELETE: (id: string) => `/rodx/job/${id}`,
    JOB_COUNT: '/rodx/job/count',

    LOCATIONS_LIST: '/rodx/locations',
    LOCATION: (id: string) => `/rodx/location/${id}`,
    LOCATION_CREATE: '/rodx/location',
    LOCATION_UPDATE: (id: string) => `/rodx/location/${id}`,

    COMPANY: '/rodx/company',
    COMPANY_CONNECTIONS: '/rodx/company/connections',

    NOTIFICATIONS: '/notification',
    NOTIFICATION_MARK_READ: (id: string) => `/notification/markAsRead/${id}`,

    PRICEBOOKS: '/rodx/pricebooks',
    PRICEBOOKS_SEARCH: '/rodx/pricebooks/search',
    PRICEBOOK_RULES: (pricebookId: string) => `/rodx/pricebook/${pricebookId}/rules`,
    PRICEBOOK_RULE: (pricebookId: string) => `/rodx/pricebook/${pricebookId}/rule`,
    PRICEBOOK_RULE_UPDATE: (pricebookId: string, ruleId: string) =>
        `/rodx/pricebook/${pricebookId}/rule/${ruleId}`,
    PRICEBOOK_RULE_DELETE: (pricebookId: string, ruleId: string) =>
        `/rodx/pricebook/${pricebookId}/rule/${ruleId}`,
    PRICEBOOK_DOCUMENTATION: '/rodx/pricebook/documentation',
    VEHICLE_MAKES: '/rodx/vehicles/makes',
    VEHICLE_MODELS: '/rodx/vehicles/models',

    ESTIMATE: '/rodx/estimate',

    CATALOG: '/rodx/catalog',
};
